import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { isDebugEnabledSelector, isDebugToolbarEnabledSelector } from '../../../../debug/debugStateSelector';
import plotSheetAnimationVelocity, {
    SheetVelocityGraphData,
} from '../../../../debug/sheetAnimations/plotSheetAnimationVelocity';

const emptyGraphData = (): SheetVelocityGraphData => ({ speedDataDragging: [], speedDataNotDragging: [] });

type SheetAnimationDebugHandlers = {
    onVelocitySample?: (velocityPxMs: number, isDragging: boolean) => void;
    onDragSettled?: () => void;
};

const NO_HANDLERS: SheetAnimationDebugHandlers = {};

/** Collects the sheet's drag and animation velocities for the debug toolbar's velocity chart. */
const useSheetAnimationDebug = (): SheetAnimationDebugHandlers => {
    const isDebugEnabled = useSelector(isDebugEnabledSelector);
    const isDebugToolbarEnabled = useSelector(isDebugToolbarEnabledSelector);

    const graphData = useRef<SheetVelocityGraphData>(emptyGraphData());
    const firstSampleTimestamp = useRef<number | null>(null);

    // Memoised because most of the sheet's re-renders happen mid-animation, where reconciling
    // fresh handlers is measurable
    const handlers = useMemo<SheetAnimationDebugHandlers>(
        () => ({
            onVelocitySample: (velocityPxMs: number, isDragging: boolean) => {
                if (firstSampleTimestamp.current === null) firstSampleTimestamp.current = performance.now();

                const point = { x: performance.now() - firstSampleTimestamp.current, y: velocityPxMs };
                const samples = isDragging
                    ? graphData.current.speedDataDragging
                    : graphData.current.speedDataNotDragging;

                samples.push(point);
            },

            onDragSettled: () => {
                plotSheetAnimationVelocity(graphData.current);

                graphData.current = emptyGraphData();
                firstSampleTimestamp.current = null;
            },
        }),
        [],
    );

    return isDebugEnabled && isDebugToolbarEnabled ? handlers : NO_HANDLERS;
};

export default useSheetAnimationDebug;
